/**
 * Session-state -> game-alert derivation.
 *
 * THE POINT OF THIS FILE: a driver playing a game must not be the last
 * to know their session has a problem. The game is a full-screen overlay
 * sitting on top of the charging UI, so any state the driver would have
 * noticed on that screen has to break THROUGH the game rather than wait
 * behind it. `alert` force-pauses the game and shows the reason.
 *
 * Pure and total on purpose — no React, no fetching — so the states that
 * matter can be asserted directly in a test rather than reproduced by
 * unplugging a real charger.
 *
 * PRECEDENCE. When several conditions hold at once the driver gets the
 * one that most changes what they should DO, not the first one matched:
 *   1. needs_force_stop  — the charger won't stop on its own; the driver
 *                          has to act, and no other message helps.
 *   2. payment failed    — money is wrong; they need to fix a card.
 *   3. charger fault     — hardware refused; unplug/replug or move.
 *   4. charger offline   — probably transient, still worth surfacing.
 *   5. session ended     — benign and expected; the weakest signal, and
 *                          the one most likely to co-occur with the
 *                          others (a fault ends the session too).
 *
 * A returned null means "nothing to say" — the caller sends no alert and
 * leaves the game running.
 */

/** The subset of session state this derivation reads. Every field is
 *  optional: the active-sessions payload is a projection and may not
 *  carry all of them, and an absent field must never be read as a
 *  problem. Only a POSITIVE signal raises an alert. */
export interface GameAlertInput {
  /** False once the session is no longer in the active list, or its
   *  status left 'active'. */
  sessionActive: boolean;
  /** Session.status verbatim when known. */
  status?: string | null;
  /** Session.needs_force_stop — the stranded-session flag. */
  needsForceStop?: boolean | null;
  /** Session.payment_status — 'capture_failed' / 'overage_failed'. */
  paymentStatus?: string | null;
  /** Charger.is_online. Undefined = unknown, NOT offline. */
  chargerOnline?: boolean | null;
  /** Latest connector status when known, e.g. 'Faulted'. */
  connectorStatus?: string | null;
}

export type GameAlertReason =
  | 'needs_force_stop'
  | 'payment_failed'
  | 'charger_faulted'
  | 'charger_offline'
  | 'session_ended';

export interface GameAlert {
  reason: GameAlertReason;
  /** i18n key under the `charging` namespace. */
  messageKey: string;
}

const PAYMENT_FAILED = new Set(['capture_failed', 'overage_failed', 'failed']);
const ENDED_STATUSES = new Set([
  'completed', 'stopped_by_user', 'stopped_by_system', 'failed', 'finishing',
]);

export function deriveGameAlert(input: GameAlertInput): GameAlert | null {
  if (input.needsForceStop === true) {
    return { reason: 'needs_force_stop', messageKey: 'game.alert.needsForceStop' };
  }
  if (input.paymentStatus && PAYMENT_FAILED.has(input.paymentStatus)) {
    return { reason: 'payment_failed', messageKey: 'game.alert.paymentFailed' };
  }
  if (input.connectorStatus === 'Faulted') {
    return { reason: 'charger_faulted', messageKey: 'game.alert.chargerFaulted' };
  }
  // Explicit false only. `undefined` means we have no charger read yet
  // (first poll in flight, or the query is disabled) and must not be
  // reported as a disconnection.
  if (input.chargerOnline === false) {
    return { reason: 'charger_offline', messageKey: 'game.alert.chargerOffline' };
  }
  if (!input.sessionActive
      || (input.status != null && ENDED_STATUSES.has(input.status))) {
    return { reason: 'session_ended', messageKey: 'game.alert.sessionEnded' };
  }
  return null;
}
